import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  signOut,
  sendPasswordResetEmail,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  deleteDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import Modal from "../components/Modal/Modal";
import { useAnalysis } from "../contexts/AnalysisContext";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { urls } from "../services/apiService";
import {
  FaEnvelope,
  FaLock,
  FaDownload,
  FaSignOutAlt,
  FaTrash,
  FaUser,
  FaCog,
  FaShieldAlt,
  FaDatabase,
  FaExclamationTriangle,
  FaArrowRight,
  FaTools // Maintenance icon
} from "react-icons/fa";
import { checkPageStatus } from "../utilityhook/pagecheck"; // ✅ import utility

const SettingsPage = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;
  const { t } = useTranslation();
  const [email, setEmail] = useState(user ? user.email : "");
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [password, setPassword] = useState("");
  const { toastMsg, handleCloseToast, setToastMsg } = useAnalysis();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isActive, setIsActive] = useState(true); // ✅ track maintenance status

  useEffect(() => {
    const fetchPageStatus = async () => {
      const active = await checkPageStatus("3-settings"); // ✅ wait for backend or localStorage
      setIsActive(active);
    };
    fetchPageStatus();
  }, []);

  const handleDashboard = () => {
    navigate("/map-selector");
  };

  useEffect(() => {
    if (user && isActive) {
      setEmail(user.email);
    }
  }, [user, isActive]);

const handleDeleteAccount = async () => {
  if (!deleteConfirm) {
    setDeleteConfirm(true);
    return;
  }

  setLoading(true);
  try {
    const db = getFirestore();
    const userDocRef = doc(db, "users", user.uid);
    const fieldsCollectionRef = collection(db, `users/${user.uid}/fields`);

    // Delete the user's fields using a batch operation
    const querySnapshot = await getDocs(fieldsCollectionRef);
    const batch = writeBatch(db);

    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();

    // Delete the user document
    await deleteDoc(userDocRef);

    // Delete the user's authentication
    await deleteUser(user);

    // Attempt backend deletion, but don't fail if it doesn't exist
    try {
      const response = await axios.post(urls.delete_user_data, {
        username: user.email,
      });
      if (response.data?.message) {
        setMessage(response.data.message);
      }
    } catch (backendError) {
      console.warn(
        "Backend user deletion failed, but Firebase account was deleted.",
        backendError
      );
    }

    setToastMsg({
      message: "Account deleted successfully",
      isShown: true,
      type: "success",
    });
    navigate("/auth");
  } catch (error) {
    if (error.code === "auth/requires-recent-login") {
      setShowReauthModal(true);
      setMessage(error.response?.data?.error || "An error occurred.");
    } else {
      console.error("Error deleting account:", error);
      setToastMsg({
        message: "Error deleting account: " + error.message,
        isShown: true,
        type: "error",
      });
    }
  } finally {
    setLoading(false);
    setDeleteConfirm(false);
  }
};


  const handleReauthenticate = async () => {
    try {
      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(user, credential);
      setShowReauthModal(false);
      await handleDeleteAccount();
    } catch (error) {
      console.error("Error reauthenticating:", error);
      setToastMsg({
        message: "Error reauthenticating: " + error.message,
        isShown: true,
        type: "error",
      });
    }
  };

  const handleRestorePassword = () => {
    if (email) {
      sendPasswordResetEmail(auth, email)
        .then(() => {
          setToastMsg({
            message: "Password reset email sent! Check your inbox.",
            isShown: true,
            type: "success",
          });
        })
        .catch((error) => {
          console.error("Error sending password reset email:", error);
          setToastMsg({
            message: "Error sending password reset email: " + error.message,
            isShown: true,
            type: "error",
          });
        });
    } else {
      setToastMsg({
        message: "Email is not available.",
        isShown: true,
        type: "error",
      });
    }
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        localStorage.setItem("Authenticate", false);
        localStorage.removeItem("User");
        localStorage.removeItem("userPlan");
        localStorage.removeItem("FieldLength");
        navigate("/auth");
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  };

  const handleDownloadFields = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const db = getFirestore();
        const fieldsCollectionRef = collection(db, `users/${user.uid}/fields`);
        const querySnapshot = await getDocs(fieldsCollectionRef);

        const fields = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        let fieldDataText = "Field Data:\n";
        fields.forEach((field) => {
          fieldDataText += `Field Name: ${field.fieldName}\n`;
          fieldDataText += `Area: ${field.area}\n`;
          fieldDataText += `Coordinates: ${field.coordinates
            .map(
              (coordinate) =>
                `(${coordinate.latitude}, ${coordinate.longitude})`
            )
            .join(", ")}\n\n`;
        });

        const blob = new Blob([fieldDataText], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "fields.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setToastMsg({
          message: "Fields data downloaded successfully!",
          isShown: true,
          type: "success",
        });
      } else {
        throw new Error("User must be authenticated to retrieve field data.");
      }
    } catch (error) {
      console.error("Error:", error);
      setToastMsg({
        message: "Error downloading fields data: " + error.message,
        isShown: true,
        type: "error",
      });
    }
  };

  // 🚧 Under Maintenance Overlay (only visible when inactive)
  if (!isActive) {
    return (
      <div className="p-4 absolute right-0 w-full lg:w-[93%] 2xl:w-[95%] overflow-x-hidden min-h-screen pb-8 bg-gray-50">
        <div className="h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 bg-opacity-90 flex flex-col items-center justify-center text-center z-[9999] backdrop-blur-sm rounded-2xl">
          <FaTools className="text-5xl text-green-500 mb-4 animate-bounce" />
          <h1 className="text-3xl md:text-5xl font-semibold text-gray-600 mb-2">
            Page Under Maintenance
          </h1>
          <p className="text-gray-500 text-lg max-w-md">
            We're currently enhancing this feature for better accuracy and
            user experience. Please check back soon!
          </p>
          <div className="mt-6">
            <div className="w-16 h-16 border-4 border-gray-600 border-t-green-500 rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 absolute right-0 w-full lg:w-[93%] 2xl:w-[95%] overflow-x-hidden min-h-screen pb-8 bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 w-full mb-8 p-6 rounded-2xl flex flex-col md:flex-row items-center justify-between text-white shadow-lg">
        <div className="text-center md:text-left flex-1">
          <h2 className="text-3xl font-bold mb-2">{t("Account Settings")} ⚙️</h2>
          <h3 className="text-xl font-semibold mb-4">
            {user?.displayName || t("User")}
          </h3>
          <p className="text-blue-100 text-lg mb-6">
            {t("Manage your account preferences and security settings")}
          </p>
          <button
            className="bg-white text-blue-600 w-full md:w-60 px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 shadow-md flex items-center justify-center gap-2"
            onClick={handleDashboard}
          >
            {t("Back to Dashboard")} <FaArrowRight />
          </button>
        </div>
        <div className="relative mt-6 md:mt-0">
          <img
            src={require("../assets/profile.jpg")}
            alt="Profile"
            className="w-32 h-32 rounded-full border-4 border-white shadow-2xl"
          />
          <div className="absolute -bottom-2 -right-2 bg-green-400 w-8 h-8 rounded-full border-4 border-white flex items-center justify-center">
            <FaUser className="text-white text-xs" />
          </div>
        </div>
      </div>

      {/* Settings Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Email Address Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <FaEnvelope className="text-blue-600 text-xl" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 text-lg">{t("Email Address")}</h3>
              <p className="text-gray-600 mt-1 break-all">{email}</p>
            </div>
          </div>
          <button className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-medium hover:bg-gray-200 transition-all duration-200 flex items-center justify-center gap-2">
            <FaCog className="text-sm" />
            {t("Update Email")}
          </button>
        </div>

        {/* Password Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <FaLock className="text-green-600 text-xl" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 text-lg">{t("Password")}</h3>
              <p className="text-gray-600 mt-1">{t("Last updated: Recently")}</p>
            </div>
          </div>
          <button 
            onClick={handleRestorePassword}
            className="w-full bg-green-500 text-white py-3 px-4 rounded-xl font-medium hover:bg-green-600 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <FaShieldAlt className="text-sm" />
            {t("Reset Password")}
          </button>
        </div>

        {/* Download Data Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <FaDownload className="text-purple-600 text-xl" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 text-lg">{t("Export Data")}</h3>
              <p className="text-gray-600 mt-1">{t("Download all your field data")}</p>
            </div>
          </div>
          <button 
            onClick={handleDownloadFields}
            className="w-full bg-purple-500 text-white py-3 px-4 rounded-xl font-medium hover:bg-purple-600 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <FaDatabase className="text-sm" />
            {t("Download Data")}
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-red-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-red-100 rounded-lg">
            <FaExclamationTriangle className="text-red-600 text-xl" />
          </div>
          <h2 className="text-xl font-bold text-red-800">{t("Danger Zone")}</h2>
        </div>
        
        <div className="space-y-4">
          {/* Logout Button */}
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-3 sm:mb-0">
              <FaSignOutAlt className="text-gray-600" />
              <div>
                <h3 className="font-semibold text-gray-800">{t("Logout")}</h3>
                <p className="text-gray-600 text-sm">{t("Sign out of your account")}</p>
              </div>
            </div>
            <button 
              onClick={handleLogout}
              className="bg-blue-500 text-white py-2 px-6 rounded-lg font-medium hover:bg-blue-600 transition-all duration-200 flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <FaSignOutAlt className="text-sm" />
              {t("Logout")}
            </button>
          </div>

          {/* Delete Account Button */}
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-center gap-3 mb-3 sm:mb-0">
              <FaTrash className="text-red-600" />
              <div>
                <h3 className="font-semibold text-red-800">{t("Delete Account")}</h3>
                <p className="text-red-600 text-sm">
                  {deleteConfirm 
                    ? t("This action cannot be undone. All your data will be permanently deleted.")
                    : t("Permanently delete your account and all data")
                  }
                </p>
              </div>
            </div>
            <button 
              onClick={handleDeleteAccount}
              className={`py-2 px-6 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 w-full sm:w-auto justify-center ${
                deleteConfirm
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-red-500 text-white hover:bg-red-600"
              }`}
              disabled={loading}
            >
              <FaTrash className="text-sm" />
              {loading ? t("Deleting...") : deleteConfirm ? t("Confirm Delete") : t("Delete Account")}
            </button>
          </div>
        </div>
      </div>

      {/* Reauthentication Modal */}
      {showReauthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-2xl max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FaLock className="text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold">{t("Reauthentication Required")}</h3>
            </div>
            <p className="text-gray-600 mb-4">
              {t("Please enter your password to confirm this action.")}
            </p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("Enter your password")}
              className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowReauthModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-400 transition-colors"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleReauthenticate}
                className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-600 transition-colors"
              >
                {t("Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Message Modal */}
      <Modal
        message={toastMsg.message}
        onClose={handleCloseToast}
        type={toastMsg.type}
        isShown={toastMsg.isShown}
      />
    </div>
  );
};

export default SettingsPage;